import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, Film, FileVideo, Image as ImageIcon, Clock,
  ShieldCheck, ScrollText, AlertTriangle, ArrowRight,
} from "lucide-react";

const TECH_SPECS = [
  { label: "Container", value: "MP4, MOV, or WebM" },
  { label: "Codec (video)", value: "H.264 or H.265 (HEVC)" },
  { label: "Codec (audio)", value: "AAC, stereo (or 5.1) — 128 kbps minimum" },
  { label: "Resolution", value: "1080p minimum · 4K accepted" },
  { label: "Aspect ratio", value: "16:9 widescreen" },
  { label: "Frame rate", value: "23.976 / 24 / 25 / 30 fps · constant" },
  { label: "Duration", value: "Up to 2 hours per file" },
  { label: "File size", value: "Up to 20 GB · resumable uploads" },
  { label: "Poster", value: "JPG or PNG · 2:3 portrait · max 10 MB" },
  { label: "Captions (optional)", value: "WebVTT (.vtt) per language" },
];

const ALLOWED = [
  "Feature films and short films that present a Christian worldview.",
  "Documentaries about African Christian leaders, churches, and movements.",
  "Worship recordings, gospel concerts, and praise testimonies.",
  "Family-friendly animation rooted in Biblical narrative.",
  "Biopics, parables, and dramatized Scripture.",
];

const DISALLOWED = [
  "Content promoting violence, occult practices, or doctrines hostile to the Christian faith.",
  "Sexually explicit material or nudity — including stylized or implied.",
  "Hate speech against any ethnicity, denomination, or nationality.",
  "Movies you don't own. We require you to own — or have written permission for — every frame and every soundtrack cue.",
  "AI-generated full features without credited human authorship.",
];

export default function SubmissionGuidelines() {
  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 lg:py-20" data-testid="submission-guidelines-page">
      <p className="text-eyebrow text-[#E65F2B]">For creators</p>
      <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-3 leading-[0.95]" data-testid="submission-headline">
        Submission <span className="text-[#E65F2B]">guidelines</span>
      </h1>
      <p className="text-white/60 text-lg max-w-3xl mt-5 leading-relaxed">
        We review every film by hand. These guidelines exist so your work reaches viewers fast — and so the catalog stays worthy of the audience we serve. Read them carefully before uploading.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10">
        <div className="card-surface p-5">
          <Clock className="h-5 w-5 text-[#EAB308] mb-2" />
          <p className="font-display font-bold">48-hour review</p>
          <p className="text-xs text-white/50 mt-1">Most submissions are reviewed within two business days. Email confirmation on every decision.</p>
        </div>
        <div className="card-surface p-5">
          <ShieldCheck className="h-5 w-5 text-green-400 mb-2" />
          <p className="font-display font-bold">Reversible takedowns</p>
          <p className="text-xs text-white/50 mt-1">Disagree with a decision? Appeal it from <Link to="/creator/appeals" className="underline">your appeals page</Link>. We respond by email.</p>
        </div>
        <div className="card-surface p-5">
          <ScrollText className="h-5 w-5 text-[#E65F2B] mb-2" />
          <p className="font-display font-bold">You keep your rights</p>
          <p className="text-xs text-white/50 mt-1">Non-exclusive, revocable license. Take your film off Christoreel any time without penalty.</p>
        </div>
      </div>

      {/* Step-by-step */}
      <section className="mt-16">
        <p className="text-eyebrow mb-4">The path from upload to public</p>
        <ol className="space-y-3">
          {[
            { n: 1, h: "Create your channel", b: "Sign in, name your channel, write a short bio. This is the brand viewers see attached to your work." },
            { n: 2, h: "Upload your film + poster", b: "Drag-and-drop or pick-frame for the poster. Uploads are resumable — close the tab safely, your progress is kept." },
            { n: 3, h: "Add optional metadata", b: "Genre, captions in any African or world language, series/episode grouping. The more context, the higher the discoverability." },
            { n: 4, h: "Human review", b: "A real moderator watches representative segments and confirms the content matches these guidelines. We approve, request edits, or — rarely — decline with a written reason." },
            { n: 5, h: "Approved & paid", b: "Approved titles go live immediately. Revenue accrues per active subscriber watch-time and shows up on your Revenue dashboard in Naira, settled monthly." },
          ].map((s) => (
            <li key={s.n} className="card-surface p-5 flex gap-4" data-testid={`step-${s.n}`}>
              <div className="h-10 w-10 rounded-full border-2 border-[#E65F2B]/40 flex items-center justify-center shrink-0 font-display font-black text-[#E65F2B]">
                {s.n}
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-bold">{s.h}</h3>
                <p className="text-sm text-white/60 mt-1">{s.b}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Content rules */}
      <section className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-surface p-6" data-testid="allowed-content">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <h3 className="font-display text-xl font-bold">What we accept</h3>
          </div>
          <ul className="space-y-2 text-sm text-white/70">
            {ALLOWED.map((t) => (
              <li key={t} className="flex gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-surface p-6 border-red-500/20" data-testid="disallowed-content">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="h-5 w-5 text-red-400" />
            <h3 className="font-display text-xl font-bold">What we don't accept</h3>
          </div>
          <ul className="space-y-2 text-sm text-white/70">
            {DISALLOWED.map((t) => (
              <li key={t} className="flex gap-2">
                <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Tech specs */}
      <section className="mt-16">
        <div className="flex items-center gap-3 mb-4">
          <FileVideo className="h-5 w-5 text-[#E65F2B]" />
          <h2 className="font-display text-3xl font-bold">Technical specifications</h2>
        </div>
        <div className="card-surface overflow-x-auto" data-testid="tech-specs-table">
          <table className="w-full text-sm">
            <tbody>
              {TECH_SPECS.map((row, i) => (
                <tr key={row.label} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                  <td className="px-5 py-3 text-white/40 font-mono text-xs uppercase tracking-wider w-1/3">{row.label}</td>
                  <td className="px-5 py-3 text-white/80">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Rights & responsibilities */}
      <section className="mt-16">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="h-5 w-5 text-[#EAB308]" />
          <h2 className="font-display text-3xl font-bold">Rights & responsibilities</h2>
        </div>
        <div className="card-surface p-6 space-y-3 text-sm text-white/70 leading-relaxed">
          <p><b className="text-white">Ownership.</b> You retain copyright on everything you upload. By submitting, you grant Christoreel a non-exclusive, worldwide license to stream and promote it.</p>
          <p><b className="text-white">Third-party material.</b> You're responsible for clearing every soundtrack cue, font, and stock clip you use. Christoreel will remove infringing content on a verified takedown request.</p>
          <p><b className="text-white">Revenue share.</b> Christoreel shares <b>60% of matured monthly subscription revenue</b> with creators, split three ways: 25% by qualified streams (≥120s watch), 20% by watch-time minutes, 15% by unique listeners. Once your monthly earnings clear ₦2,000, we auto-transfer to your registered NUBAN via Paystack. Sub-threshold balances carry forward until you cross it. See the full breakdown in <Link to="/creator/revenue" className="underline hover:text-[#E65F2B]">your Revenue dashboard</Link>, or view <Link to="/how-payouts-work" className="underline hover:text-[#E65F2B]">recent unit rates and genre leaderboards</Link>.</p>
          <p><b className="text-white">Account suspension.</b> Repeated violations (or a single severe one) may suspend your channel. Suspensions are reversible — we'll always tell you why and how to come back.</p>
          <p><b className="text-white">Withdrawal.</b> Remove a title any time from the creator dashboard. Pending revenue is paid in the next settlement cycle.</p>
        </div>
      </section>

      {/* Final CTA */}
      <div className="card-surface p-8 sm:p-10 mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <p className="font-display text-2xl font-bold">Ready to put your work in front of African Christian viewers?</p>
          <p className="text-sm text-white/40 mt-1">Set up a channel and your first upload in less than ten minutes.</p>
        </div>
        <Link to="/creator/upload" className="ember-btn whitespace-nowrap" data-testid="submission-cta-upload">
          Start uploading <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
